/**
 * EnemyVisuals — extracted VFX/display helpers.
 * Each function takes the Enemy instance as the first parameter.
 */
import Phaser from 'phaser';
import { GRADE_MODIFIERS } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import { EventBus, GameEvents } from '@/events/EventBus';
import { SoundManager } from '@/systems/SoundManager';
import { Enemy } from '@/entities/Enemy';

// ────────────────────────────────────────────────────────
// Cloak
// ────────────────────────────────────────────────────────

export function updateCloak(enemy: Enemy, dt: number): void {
  if (enemy.cloakDuration <= 0) return;

  const fadeSpeed = 2.5;
  const bx = enemy.body.x;
  const by = enemy.body.y;
  const vr = enemy.visualRadius;

  // ── Detect transitions and fire VFX bursts ──
  if (enemy.isCloaked && !enemy.wasCloaked) {
    // Just activated cloak → engage VFX
    enemy.wasCloaked = true;
    cloakEngageVFX(enemy, bx, by, vr);
  } else if (!enemy.isCloaked && enemy.wasCloaked && enemy.cloakAlpha >= 1) {
    // Fully decloaked → disengage VFX
    enemy.wasCloaked = false;
    cloakDisengageVFX(enemy, bx, by, vr);
  }

  if (enemy.isCloaked) {
    enemy.cloakTimer -= dt;

    // Smooth fade toward ghost alpha
    const targetAlpha = 0.08;
    enemy.cloakAlpha = Math.max(targetAlpha, enemy.cloakAlpha - fadeSpeed * dt);

    // Hologram flicker + rare glitch flash
    const flicker = 0.04 * Math.sin(enemy.cloakTimer * 10);
    const glitch = Math.random() < 0.03 ? 0.15 : 0;
    enemy.body.setAlpha(enemy.cloakAlpha + flicker + glitch);

    // Blue hologram overlay
    if (enemy.cloakOverlay) {
      enemy.cloakOverlay.setPosition(bx, by);
      enemy.cloakOverlay.setAlpha(0.08 + 0.04 * Math.sin(enemy.cloakTimer * 6) + glitch * 0.3);
      enemy.cloakOverlay.y += Math.sin(enemy.cloakTimer * 15) * 0.5;
    }

    enemy.body.setTint(0x6699cc);

    // Periodic hologram scan-lines (horizontal bars sweeping over sprite)
    if (Math.random() < 0.06) {
      const lineY = by - vr + Math.random() * vr * 2;
      const line = enemy.scene.add.rectangle(bx, lineY, vr * 2.5, 1, 0x44aaff, 0.3)
        .setDepth(enemy.body.depth + 1).setBlendMode(Phaser.BlendModes.ADD);
      enemy.scene.tweens.add({
        targets: line, alpha: 0, scaleY: 0.2,
        duration: 150 + Math.random() * 100,
        onComplete: () => line.destroy(),
      });
    }

    // Periodic tiny hex-sparkles around the silhouette
    if (Math.random() < 0.08) {
      const angle = Math.random() * Math.PI * 2;
      const r = vr * (0.6 + Math.random() * 0.5);
      const sp = enemy.scene.add.text(bx + Math.cos(angle) * r, by + Math.sin(angle) * r, '⬡', {
        fontSize: '5px', color: '#4488cc',
      }).setOrigin(0.5).setAlpha(0.6).setDepth(enemy.body.depth + 1).setBlendMode(Phaser.BlendModes.ADD);
      enemy.scene.tweens.add({
        targets: sp, alpha: 0, scale: 2,
        duration: 300 + Math.random() * 200,
        onComplete: () => sp.destroy(),
      });
    }

    // Fade out bars/label
    const barAlpha = Math.max(0, enemy.cloakAlpha - 0.08) * 5;
    enemy.hpBar.setAlpha(barAlpha);
    enemy.hpBarBg.setAlpha(barAlpha);
    enemy.label.setAlpha(barAlpha);

    if (enemy.cloakTimer <= 0) {
      enemy.isCloaked = false;
      enemy.cloakCooldown = enemy.cloakCooldownMax;
    }
  } else {
    // Smooth fade back to visible
    if (enemy.cloakAlpha < 1) {
      enemy.cloakAlpha = Math.min(1, enemy.cloakAlpha + fadeSpeed * dt);
      enemy.body.setAlpha(enemy.cloakAlpha * enemy.fogVisibility);

      if (enemy.cloakOverlay) {
        enemy.cloakOverlay.setPosition(bx, by);
        enemy.cloakOverlay.setAlpha(Math.max(0, (1 - enemy.cloakAlpha) * 0.12));
      }

      if (enemy.cloakAlpha >= 1) {
        const gradeTint = GRADE_MODIFIERS[enemy.grade].tint;
        if (gradeTint !== 0) { enemy.body.setTint(gradeTint); } else { enemy.body.clearTint(); }
        if (enemy.cloakOverlay) enemy.cloakOverlay.setAlpha(0);
        if (enemy.fogVisible) {
          enemy.hpBar.setAlpha(1);
          enemy.hpBarBg.setAlpha(1);
          enemy.label.setAlpha(1);
        }
      }
    }

    enemy.cloakCooldown -= dt;
    if (enemy.cloakCooldown <= 0) {
      enemy.isCloaked = true;
      enemy.cloakTimer = enemy.cloakDuration;
    }
  }
}

/** VFX burst when cloaking engages — implosion ring + dissolving particles. */
export function cloakEngageVFX(enemy: Enemy, x: number, y: number, radius: number): void {
  const scene = enemy.scene;
  const depth = enemy.body.depth + 2;

  // Contracting ring (implosion feel)
  const ring = scene.add.circle(x, y, radius * 2.5, 0x4488ff, 0)
    .setStrokeStyle(2, 0x4488ff, 0.6).setDepth(depth).setBlendMode(Phaser.BlendModes.ADD);
  scene.tweens.add({
    targets: ring,
    scaleX: 0.1, scaleY: 0.1, alpha: 0,
    duration: 350, ease: 'Quad.easeIn',
    onComplete: () => ring.destroy(),
  });

  // Dissolving pixel particles flying inward
  for (let i = 0; i < 14; i++) {
    const angle = (Math.PI * 2 * i) / 14 + (Math.random() - 0.5) * 0.3;
    const dist = radius * (1.5 + Math.random());
    const px = x + Math.cos(angle) * dist;
    const py = y + Math.sin(angle) * dist;
    const size = 1 + Math.random() * 2;
    const dot = scene.add.rectangle(px, py, size, size, 0x66bbff, 0.8)
      .setDepth(depth).setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: dot,
      x: x + (Math.random() - 0.5) * 4, y: y + (Math.random() - 0.5) * 4,
      alpha: 0, scaleX: 0.2, scaleY: 0.2,
      duration: 250 + Math.random() * 150, ease: 'Quad.easeIn',
      onComplete: () => dot.destroy(),
    });
  }

  // Blue flash on body
  const flash = scene.add.circle(x, y, radius * 1.2, 0x4488ff, 0.3)
    .setDepth(depth).setBlendMode(Phaser.BlendModes.ADD);
  scene.tweens.add({
    targets: flash,
    alpha: 0, scaleX: 0.5, scaleY: 0.5,
    duration: 200,
    onComplete: () => flash.destroy(),
  });

  // Vertical scan-line sweep
  for (let j = 0; j < 3; j++) {
    const lineY = y - radius + (radius * 2 * j) / 2;
    const line = scene.add.rectangle(x, lineY, radius * 3, 1.5, 0x88ccff, 0.5)
      .setDepth(depth).setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: line,
      y: y, alpha: 0, scaleX: 0.1,
      duration: 200 + j * 60, ease: 'Quad.easeIn',
      onComplete: () => line.destroy(),
    });
  }
}

/** VFX burst when decloaking — explosion ring + particles flying outward. */
export function cloakDisengageVFX(enemy: Enemy, x: number, y: number, radius: number): void {
  const scene = enemy.scene;
  const depth = enemy.body.depth + 2;

  // Expanding ring (reveal pulse)
  const ring = scene.add.circle(x, y, 2, 0x44ddff, 0)
    .setStrokeStyle(2.5, 0x44ddff, 0.7).setDepth(depth).setBlendMode(Phaser.BlendModes.ADD);
  scene.tweens.add({
    targets: ring,
    scaleX: radius * 0.15, scaleY: radius * 0.15, alpha: 0,
    duration: 400, ease: 'Quad.easeOut',
    onComplete: () => ring.destroy(),
  });

  // Particles flying outward (materialization sparks)
  for (let i = 0; i < 16; i++) {
    const angle = (Math.PI * 2 * i) / 16 + (Math.random() - 0.5) * 0.3;
    const size = 1 + Math.random() * 2.5;
    const col = Math.random() > 0.5 ? 0x88ddff : 0x4488ff;
    const dot = scene.add.rectangle(x, y, size, size, col, 0.9)
      .setDepth(depth).setBlendMode(Phaser.BlendModes.ADD)
      .setAngle(Math.random() * 360);
    const dist = radius * (1.2 + Math.random() * 1.5);
    scene.tweens.add({
      targets: dot,
      x: x + Math.cos(angle) * dist, y: y + Math.sin(angle) * dist,
      alpha: 0, scaleX: 0.3, scaleY: 0.3,
      duration: 300 + Math.random() * 200, ease: 'Quad.easeOut',
      onComplete: () => dot.destroy(),
    });
  }

  // Bright center flash
  const flash = scene.add.circle(x, y, radius * 0.8, 0xaaddff, 0.4)
    .setDepth(depth).setBlendMode(Phaser.BlendModes.ADD);
  scene.tweens.add({
    targets: flash,
    scaleX: 2, scaleY: 2, alpha: 0,
    duration: 300, ease: 'Quad.easeOut',
    onComplete: () => flash.destroy(),
  });

  // Horizontal scan-lines expanding outward
  for (let j = 0; j < 4; j++) {
    const line = scene.add.rectangle(x, y, 2, 1.5, 0x88ccff, 0.5)
      .setDepth(depth).setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: line,
      scaleX: radius * 2, alpha: 0,
      y: y - radius + (radius * 2 * j) / 3,
      duration: 250 + j * 50, ease: 'Quad.easeOut',
      onComplete: () => line.destroy(),
    });
  }
}

// ────────────────────────────────────────────────────────
// Burn
// ────────────────────────────────────────────────────────

/** Tick burn DOT: damage, particles, sound. */
export function updateBurn(enemy: Enemy, dt: number): void {
  if (enemy.burnTimer <= 0) return;
  enemy.burnTimer -= dt;

  // Damage tick every 0.5s
  enemy.burnTickTimer += dt;
  if (enemy.burnTickTimer >= 0.5) {
    enemy.burnTickTimer -= 0.5;
    const dmg = enemy.burnDps * 0.5; // half-second of DPS
    enemy.hp -= dmg;
    enemy.spawnDamageNumber(dmg, 1.5); // red-ish damage number
    if (enemy.hp <= 0) {
      enemy.hp = 0;
      enemy.destroy();
      EventBus.emit(GameEvents.ENEMY_DESTROYED, enemy);
      return;
    }
  }

  // Sizzle sound every ~1.5s
  enemy.burnSizzleTimer -= dt;
  if (enemy.burnSizzleTimer <= 0) {
    enemy.burnSizzleTimer = 1.2 + Math.random() * 0.6;
    SoundManager.play('burnSizzle');
  }

  // Fire + smoke particles (spawn a few per frame)
  if (Math.random() < 0.35) {
    spawnBurnParticle(enemy, true);  // fire
  }
  if (Math.random() < 0.25) {
    spawnBurnParticle(enemy, false); // smoke
  }

  // Orange tint while burning
  if (enemy.burnTimer > 0) {
    enemy.body.setTint(0xff6622);
  }

  // Burn expired
  if (enemy.burnTimer <= 0) {
    enemy.burnTimer = 0;
    enemy.burnDps = 0;
    enemy.burnTickTimer = 0;
    // Restore grade tint
    const gradeTint = GRADE_MODIFIERS[enemy.grade].tint;
    if (gradeTint !== 0) enemy.body.setTint(gradeTint);
    else enemy.body.clearTint();
  }
}

/** Spawn a single fire or smoke particle at the enemy position. */
export function spawnBurnParticle(enemy: Enemy, isFire: boolean): void {
  const x = enemy.body.x + (Math.random() - 0.5) * enemy.visualRadius * 1.2;
  const y = enemy.body.y + (Math.random() - 0.5) * enemy.visualRadius * 0.8;
  const size = isFire ? 2 + Math.random() * 3 : 3 + Math.random() * 4;
  const color = isFire
    ? (Math.random() < 0.5 ? 0xff6600 : 0xffaa00) // orange or yellow
    : (Math.random() < 0.5 ? 0x555555 : 0x222222); // gray or dark smoke
  const alpha = isFire ? 0.7 : 0.4;

  const p = enemy.scene.add.rectangle(x, y, size, size, color, alpha)
    .setDepth(enemy.body.depth + 1);

  // Fire rises fast, smoke rises slower and drifts
  const riseY = isFire ? y - 8 - Math.random() * 10 : y - 12 - Math.random() * 8;
  const driftX = isFire ? x + (Math.random() - 0.5) * 4 : x + (Math.random() - 0.5) * 10;
  const dur = isFire ? 200 + Math.random() * 150 : 400 + Math.random() * 300;

  enemy.burnParticles.push(p);
  enemy.scene.tweens.add({
    targets: p,
    x: driftX,
    y: riseY,
    alpha: 0,
    scaleX: isFire ? 0.3 : 1.5,
    scaleY: isFire ? 0.3 : 1.5,
    duration: dur,
    onComplete: () => {
      p.destroy();
      const idx = enemy.burnParticles.indexOf(p);
      if (idx >= 0) enemy.burnParticles.splice(idx, 1);
    },
  });
}

// ────────────────────────────────────────────────────────
// Hit sparks & popups
// ────────────────────────────────────────────────────────

/** Spawn a hit spark VFX at the enemy position. */
export function spawnHitSpark(enemy: Enemy, dmg: number): void {
  // Scale 0.08–0.25 based on damage (halved from raw sprite size)
  const baseScale = 0.12;
  const dmgScale = Math.min(dmg / 100, 1); // normalize: 100 dmg = max
  const scale = baseScale + dmgScale * 0.13;
  const ox = (Math.random() - 0.5) * 10;
  const oy = (Math.random() - 0.5) * 10;
  const spark = enemy.scene.add.image(enemy.body.x + ox, enemy.body.y + oy, 'fx_spark')
    .setScale(scale)
    .setRotation(Math.random() * Math.PI * 2)
    .setDepth(25)
    .setAlpha(0.9);
  enemy.scene.tweens.add({
    targets: spark,
    alpha: 0,
    scaleX: scale * 1.5,
    scaleY: scale * 1.5,
    duration: 120 + Math.random() * 60,
    onComplete: () => spark.destroy(),
  });
}

/** Show "COMBO x1.5" style popup when a new weapon type hits. */
export function spawnComboPopup(enemy: Enemy, types: number, comboMult: number): void {
  const label = `COMBO ×${comboMult.toFixed(1)}`;
  const colors = ['', '', '#44ffaa', '#ffcc44', '#ff44ff']; // 2,3,4+ types
  const color = colors[Math.min(types, colors.length - 1)] ?? '#ff44ff';
  const x = enemy.body.x;
  const y = enemy.body.y - enemy.visualRadius - 20;
  const txt = enemy.scene.add.text(x, y, label, {
    fontFamily: 'monospace', fontSize: '12px', fontStyle: 'bold',
    color, stroke: '#000000', strokeThickness: 3,
  }).setOrigin(0.5).setDepth(52);

  enemy.scene.tweens.add({
    targets: txt,
    y: y - 30, alpha: 0, scale: 1.3,
    duration: 1200, ease: 'Power2',
    onComplete: () => txt.destroy(),
  });

  // Sound + event for UI
  SoundManager.play('combo');
  EventBus.emit(GameEvents.WEAPON_COMBO, { enemy, types, mult: comboMult });
}

/** Show "!" alert popup above enemy head on first robot detection. */
export function showAlertPopup(enemy: Enemy): void {
  const alertX = enemy.body.x;
  const alertY = enemy.body.y - enemy.visualRadius - 16;
  const txt = enemy.scene.add.text(alertX, alertY, '!', {
    fontSize: '18px', fontFamily: 'monospace', fontStyle: 'bold',
    color: '#ff4444', stroke: '#000000', strokeThickness: 3,
  }).setOrigin(0.5).setDepth(enemy.body.depth + 5);
  // Pop-in scale
  txt.setScale(0);
  enemy.scene.tweens.add({
    targets: txt,
    scaleX: 1.3, scaleY: 1.3,
    duration: 120,
    ease: 'Back.easeOut',
    onComplete: () => {
      enemy.scene.tweens.add({
        targets: txt,
        scaleX: 1, scaleY: 1, y: alertY - 12, alpha: 0,
        duration: 600, delay: 300,
        ease: 'Cubic.easeOut',
        onComplete: () => txt.destroy(),
      });
    },
  });
}

/** Show "?" confusion popup when enemy senses a cloaked robot. */
export function showConfusionPopup(enemy: Enemy): void {
  const alertX = enemy.body.x;
  const alertY = enemy.body.y - enemy.visualRadius - 16;
  const txt = enemy.scene.add.text(alertX, alertY, '?', {
    fontSize: '18px', fontFamily: 'monospace', fontStyle: 'bold',
    color: '#44ccff', stroke: '#000000', strokeThickness: 3,
  }).setOrigin(0.5).setDepth(enemy.body.depth + 5);
  txt.setScale(0);
  enemy.scene.tweens.add({
    targets: txt,
    scaleX: 1.3, scaleY: 1.3,
    duration: 120,
    ease: 'Back.easeOut',
    onComplete: () => {
      enemy.scene.tweens.add({
        targets: txt,
        scaleX: 1, scaleY: 1, y: alertY - 12, alpha: 0,
        duration: 800, delay: 500,
        ease: 'Cubic.easeOut',
        onComplete: () => txt.destroy(),
      });
    },
  });
}

// ────────────────────────────────────────────────────────
// Sprite stacking
// ────────────────────────────────────────────────────────

/** Sprite stacking: offset layered copies vertically with wave animation when moving. */
export function updateSpriteStack(enemy: Enemy, dt: number): void {
  const dx = enemy.body.x - enemy.prevBodyX;
  const dy = enemy.body.y - enemy.prevBodyY;
  const moved = Math.sqrt(dx * dx + dy * dy);
  enemy.prevBodyX = enemy.body.x;
  enemy.prevBodyY = enemy.body.y;

  // Speed factor: how much the enemy moved this frame (0→1 intensity)
  const speedFactor = Math.min(moved / (Enemy.STACK_MIN_SPEED * 2), 1);
  const isMoving = moved > Enemy.STACK_MIN_SPEED;

  enemy.stackPhase += dt * 4; // wave animation speed

  for (let i = 0; i < enemy.spriteStackLayers.length; i++) {
    const layer = enemy.spriteStackLayers[i];
    const layerIndex = i + 1; // 1-based for offset calc

    if (isMoving && enemy.fogVisible) {
      // Vertical offset: each layer stacks higher, with gentle wave
      const wave = Math.sin(enemy.stackPhase + layerIndex * 0.8) * 1.5;
      const yOff = -layerIndex * Enemy.STACK_LAYER_SPACING + wave;

      // Slight horizontal trail offset (opposite of movement direction)
      const trailX = -dx * layerIndex * 0.3;
      const trailY = -dy * layerIndex * 0.3;

      layer.setPosition(
        enemy.body.x + trailX,
        enemy.body.y + yOff + trailY,
      );
      layer.setScale(enemy.body.scaleX * (1 + layerIndex * 0.02));
      layer.setFlipX(enemy.body.flipX);

      // Alpha fades with height: top layers are more transparent
      const targetAlpha = (0.35 - layerIndex * 0.06) * speedFactor;
      layer.setAlpha(Math.max(0, targetAlpha));

      // Cloak: hide stack layers when cloaked
      if (enemy.isCloaked) {
        layer.setAlpha(layer.alpha * enemy.cloakAlpha * 0.3);
      }
    } else {
      // Fade out when stopped
      if (layer.alpha > 0.01) {
        layer.setAlpha(layer.alpha * 0.85);
        layer.setPosition(enemy.body.x, enemy.body.y - layerIndex * Enemy.STACK_LAYER_SPACING);
      } else {
        layer.setAlpha(0);
      }
    }
  }
}

// ────────────────────────────────────────────────────────
// Terrain death
// ────────────────────────────────────────────────────────

/** Begin terrain death animation. Enemy stops all behavior and drains HP over duration. */
export function startTerrainDeath(enemy: Enemy, type: 'water' | 'magma' | 'chasm'): void {
  if (enemy.terrainDyingType) return; // already dying
  enemy.terrainDyingType = type;
  const durations = { water: GOD.terrainDeathWater, magma: GOD.terrainDeathMagma, chasm: GOD.terrainDeathChasm };
  enemy.terrainDyingDuration = durations[type];
  enemy.terrainDyingTimer = enemy.terrainDyingDuration;

  // Play terrain-specific SFX
  const sfxMap = { water: 'terrainDrown', magma: 'terrainBurn', chasm: 'terrainFall' } as const;
  SoundManager.play(sfxMap[type]);

  // Emit event for JuiceEffects visual animation
  EventBus.emit(GameEvents.TERRAIN_DEATH, { enemy, type, duration: enemy.terrainDyingDuration });
}

/** Tick terrain death: drain HP proportionally, then destroy when timer expires. */
export function updateTerrainDying(enemy: Enemy, dt: number): void {
  if (!enemy.terrainDyingType) return;

  // Drain HP proportionally over the duration
  const drainPerSec = enemy.maxHp / enemy.terrainDyingDuration;
  enemy.hp = Math.max(0, enemy.hp - drainPerSec * dt);

  enemy.terrainDyingTimer -= dt;

  // Update HP bar during death
  updateHpBar(enemy);

  if (enemy.terrainDyingTimer <= 0 || enemy.hp <= 0) {
    enemy.hp = 0;
    enemy.destroy();
    EventBus.emit(GameEvents.ENEMY_DESTROYED, enemy);
  }
}

/** Update HP bar visuals (extracted for reuse during terrain death). */
export function updateHpBar(enemy: Enemy): void {
  const ratio = Math.max(0, enemy.hp / enemy.maxHp);
  const barW = enemy.visualRadius * 1.6;
  enemy.hpBar.width = barW * ratio;
  // Color: green → yellow → red
  const r = ratio < 0.5 ? 255 : Math.round(255 * (1 - ratio) * 2);
  const g = ratio > 0.5 ? 255 : Math.round(255 * ratio * 2);
  enemy.hpBar.setFillStyle(Phaser.Display.Color.GetColor(r, g, 0));
}
